/**
 * Godot's 15-light cap on each item and its 256-light budget for a render, run once a frame ahead
 * of the light pass. Only an item on a placement where a light can drop is measured, and it
 * re-renders only when its list changes.
 */
import * as THREE from 'three';
import { computeWorldBoundingBox } from '../bounds.js';
import { MAX_LIGHTS_PER_RENDER } from '../../godot/rendering/rendering.js';
import { reachesCanvasLayer } from './lightCullKey.js';
import { directionalListOrder, itemPositionalLights, MAX_POSITIONAL_LIGHTS_PER_ITEM, positionalListOrder, placementId, positionalCandidates, } from './itemLightList.js';
import { rect2Intersects, rect2Merge } from '../../godot/math/rect2.js';
import { perObject } from '../../utils/perObject.js';
import { sameElements } from '../../utils/sameElements.js';
/** An entry clears when its `lights` state is collected. */
const crowdingOf = perObject((lights) => {
    return {
        canExceedBudget: lights.size > MAX_LIGHTS_PER_RENDER,
        positionalOrder: positionalListOrder(lights),
        directionalOrder: directionalListOrder(lights),
        candidates: new Map(),
    };
});
/** Whether a light can drop: more positional lights than an item takes, or lights than a render. */
function canDrop({ canExceedBudget, positionalOrder }) {
    return canExceedBudget || positionalOrder.length > MAX_POSITIONAL_LIGHTS_PER_ITEM;
}
/** The candidates at `placement` while they crowd it or the budget can drop one, else null. */
function crowdedCandidates(lights, crowding, placement) {
    const id = placementId(placement);
    let candidates = crowding.candidates.get(id);
    if (candidates === undefined) {
        const all = positionalCandidates(lights, crowding.positionalOrder, placement);
        candidates = all.length > MAX_POSITIONAL_LIGHTS_PER_ITEM || crowding.canExceedBudget ? all : null;
        crowding.candidates.set(id, candidates);
    }
    return candidates;
}
/** The roles whose quad covers a light's whole reach. */
const REACH_ROLES = new Set(['lit', 'unshadowed']);
/** Scratch for `worldRect`, read back into a `Rect2` before the next item writes it. */
const box = new THREE.Box3();
/** A `Box3` as a `Rect2`. An empty box gives an inverted rect, which meets none. */
function boxRect({ min, max }) {
    return { x: min.x, y: min.y, w: max.x - min.x, h: max.y - min.y };
}
/** `userData` on the object of a lit item, whose geometry the item above it does not count. */
export const LIT_ITEM_USER_DATA = { litItem: true };
const isLitItem = (object) => object.userData.litItem === true;
/** The world rect of `object`'s geometry, less that of the lit items nested inside it. */
function worldRect(object) {
    if (object)
        computeWorldBoundingBox(object, box, isLitItem);
    else
        box.makeEmpty();
    return boxRect(box);
}
/**
 * Each listed light's rect, `Light2D`'s `rect_cache`: where its quads land. A light whose rect
 * misses `viewport` is off Godot's list (`renderer_viewport.cpp:390,470`), so it has none here.
 */
function lightRects(passMeshes, viewport) {
    const boxes = new Map();
    for (const { mesh, ordinal, role } of passMeshes) {
        if (!REACH_ROLES.has(role))
            continue;
        const quad = computeWorldBoundingBox(mesh);
        const previous = boxes.get(ordinal);
        boxes.set(ordinal, previous ? previous.union(quad) : quad);
    }
    const rects = new Map();
    for (const [ordinal, lightBox] of boxes) {
        const rect = boxRect(lightBox);
        if (rect2Intersects(rect, viewport))
            rects.set(ordinal, rect);
    }
    return rects;
}
/**
 * The budget of the canvas on `layer`. Godot lists the lights in range of the layer
 * (`renderer_viewport.cpp:667-690`), the directional ones first, and drops the listed positional
 * lights whose rect meets the viewport past what they leave (`renderer_canvas_render_rd.cpp:592`).
 */
function layerBudget(lights, { positionalOrder, directionalOrder }, rects, layer) {
    const reached = (ordinal) => reachesCanvasLayer(lights.get(ordinal).reach, layer);
    const reachedDirectional = directionalOrder.filter(reached);
    const directional = reachedDirectional.slice(0, MAX_LIGHTS_PER_RENDER);
    const dropsDirectional = directional.length < reachedDirectional.length;
    const budget = MAX_LIGHTS_PER_RENDER - directional.length;
    const overBudget = new Set();
    let listed = 0;
    for (const ordinal of positionalOrder) {
        if (rects.has(ordinal) && reached(ordinal) && ++listed > budget)
            overBudget.add(ordinal);
    }
    return { directional, dropsDirectional, overBudget };
}
/**
 * What one frame works out once and shares between its items: the rect of each light, the budget
 * of each canvas layer, and the lists the items of one placement take alike.
 */
class FrameBudget {
    lights;
    crowding;
    rects;
    budgets = new Map();
    withinBudgets = new Map();
    wholeLists = new Map();
    constructor(lights, crowding, rects) {
        this.lights = lights;
        this.crowding = crowding;
        this.rects = rects;
    }
    /**
     * The list `item` takes from its `candidates`, or null where it reads its placement's list. An
     * item's list is its whole list: the positional lights it takes and the directional lights its
     * layer lists.
     */
    listOf(item, candidates) {
        const { directional, dropsDirectional, overBudget } = this.budgetAt(item.placement.layer);
        const taken = this.withinBudget(candidates, overBudget);
        const dropsLight = taken !== candidates || dropsDirectional;
        if (!dropsLight && candidates.length <= MAX_POSITIONAL_LIGHTS_PER_ITEM)
            return null;
        const itemRect = worldRect(item.geometry.current);
        const positional = itemPositionalLights(taken, itemRect, this.rects) ?? (dropsLight ? taken : null);
        if (!positional)
            return null;
        const list = positional === taken ? this.wholeList(taken, directional) : [...positional, ...directional];
        return { list, itemRect };
    }
    budgetAt(layer) {
        let budget = this.budgets.get(layer);
        if (!budget)
            this.budgets.set(layer, (budget = layerBudget(this.lights, this.crowding, this.rects, layer)));
        return budget;
    }
    /** `candidates` without the lights over budget. */
    withinBudget(candidates, overBudget) {
        if (overBudget.size === 0)
            return candidates;
        let taken = this.withinBudgets.get(candidates);
        if (!taken) {
            const kept = candidates.filter((ordinal) => !overBudget.has(ordinal));
            taken = kept.length === candidates.length ? candidates : kept;
            this.withinBudgets.set(candidates, taken);
        }
        return taken;
    }
    /**
     * The same array for every item that takes the placement's `taken`, so its id is built once. A
     * placement sits on one canvas layer, so the directional lights of `taken` are that layer's.
     */
    wholeList(taken, directional) {
        let list = this.wholeLists.get(taken);
        if (!list)
            this.wholeLists.set(taken, (list = [...taken, ...directional]));
        return list;
    }
}
function sameLights(a, b) {
    if (a === b)
        return true;
    if (a === null || b === null)
        return false;
    return sameElements(a, b);
}
/** Hands each item its light list this frame, where it changed, and records its window. */
export function capItemLights({ lights, items, passMeshes, viewport, windows }) {
    const crowding = crowdingOf(lights);
    const isCrowded = canDrop(crowding);
    let frame = null;
    windows.clear();
    for (const [item, handed] of items) {
        const candidates = isCrowded ? crowdedCandidates(lights, crowding, item.placement) : null;
        let list = null;
        if (candidates) {
            frame ??= new FrameBudget(lights, crowding, lightRects(passMeshes, viewport));
            const capped = frame.listOf(item, candidates);
            if (capped) {
                list = capped.list;
                recordWindow(windows, placementId({ ...item.placement, takenLights: list }), capped.itemRect);
            }
        }
        if (sameLights(list, handed))
            continue;
        items.set(item, list);
        item.take(list);
    }
}
function recordWindow(windows, id, itemRect) {
    const window = windows.get(id);
    windows.set(id, window ? rect2Merge(window, itemRect) : itemRect);
}
//# sourceMappingURL=itemLightCap.js.map